import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { Tabs, Tab } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button as MuiButton,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import PageHeader1 from '../../components/common/PageHeader1';
import { fetchCourse } from '../../Redux/Reducers/CourseReducer';
import { useDispatch, useSelector } from 'react-redux';
import { usePermissions } from '../../context/AuthContext';

function CourseList() {
    const { can } = usePermissions();
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const course = useSelector((state) => state.CourseReducer?.course);

    useEffect(() => {
        dispatch(fetchCourse());
    }, [dispatch]);

    function deleteCourse(id) {
        const token = localStorage.getItem("vedhvidhi-admin-token");
        axios.delete(`${process.env.REACT_APP_BASEURL}/admin/course/${id}`, {
            headers: {
                Authorization: token,
            }
        })
            .then(() => {
                dispatch(fetchCourse());
            })
            .catch((error) => {
                console.error("Error deleting course:", error);
            });
    }

    const handleClickOpen = (userId) => {
        setSelectedUserId(userId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedUserId(null);
    };

    const handleDelete = () => {
        if (selectedUserId) {
            deleteCourse(selectedUserId);
        }
        handleClose();
    };

    const columns = [
        { field: 'sono', headerName: 'S.No', width: 150 },
        { field: 'title', headerName: 'Course Title', width: 250 },
        { field: 'batchDetails', headerName: 'No. of batches', width: 250 },
        { field: `tutorsName`, headerName: `Tutor's name`, width: 300 },
        { field: "createdAt", headerName: "Created At", width: 200 },
        { field: "updatedAt", headerName: "Updated At", width: 200 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: ({ row }) => (
                <div>
                    {can('course_management:edit') && (
                        <Tooltip title="Edit Course">
                            <IconButton
                                component={Link}
                                to={{
                                    pathname: `${process.env.PUBLIC_URL}/course-edit/${row?._id}`,
                                    state: { step: row?.step || 0 },
                                }}
                            >
                                <i className="icofont-edit text-info"></i>
                            </IconButton>
                        </Tooltip>
                    )}
                    {can('course_management:delete') && (
                        <Tooltip title="Delete Course">
                            <IconButton component="button" onClick={() => handleClickOpen(row._id)}>
                                <i className="icofont-ui-delete text-danger"></i>
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            ),
        },
    ];

    const rows = Array.isArray(course?.data) ? course.data?.map((item, index) => ({
        sono: index + 1,
        title: item?.title ?? '-',
        batchDetails: !item?.isDraft && item?.batchDetails?.length > 0
            ? `${item?.batchDetails?.length}`
            : item?.isDraft && item?.step >= 2
                ? `${item?.batchDetails?.length}`
                : '',
        tutorsName: item?.tutorsName ?? '-',
        status: item?.isDraft ? 'Draft' : 'Uploaded',
        _id: item?._id,
        createdAt: item?.createdAt
            ? new Date(item?.createdAt).toLocaleString()
            : "",
        updatedAt: item?.updatedAt
            ? new Date(item?.updatedAt).toLocaleString()
            : "",
    })) : [];

    const filteredRows = rows.filter(row => {
        if (activeTab === 0) return row.status === 'Uploaded';
        if (activeTab === 1) return row.status === 'Draft';
        return true;
    });

    return (
        <div className="body d-flex py-1">
            <div className="container-xxl">
                <PageHeader1
                    righttitle='Add Course'
                    link='/course-add'
                    routebutton={can('course_management:add')}
                    pagetitle='Course Management List'
                />

                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ gap: 10 }} >
                    <Tab label="Course Upload" />
                    <Tab label="Draft" />
                </Tabs>

                <div className="row g-0 mb-3 mt-4">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                <Box sx={{ width: 1 }}>
                                    <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        {loading ? (
                                            <CircularProgress />
                                        ) : (
                                            <DataGrid
                                                columns={columns}
                                                rows={filteredRows}
                                                disableColumnFilter
                                                disableDensitySelector
                                                slots={{ toolbar: GridToolbar }}
                                                slotProps={{ toolbar: { showQuickFilter: true } }}
                                                disableRowSelectionOnClick
                                                pageSizeOptions={[10, 25, 50, 100]}
                                                getRowId={(row) => row._id}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Confirmation"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this course?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleClose}>
                        Cancel
                    </MuiButton>
                    <MuiButton onClick={handleDelete} color="error" autoFocus>
                        Delete
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default CourseList;